import { executeStatefulServerSideGuards } from '../helper/executeServerSideGuards';
import { generateInitialServerSideGuards } from './onboarding/generateInitialServerSideGuards';
import { INITIAL_DEFAULT_STATE } from './onboarding/state';

import type { GetServerSidePropsContext } from 'next';
import type { GetServerSidePropsWrapperProps } from '../../types';

/**
 * Onboarding GSSP 래퍼
 *
 * ℹ️ `GetServerSidePropsWrapperProps`는 `serviceRouteGuards`도 포함하지만,
 * Onboarding은 단일 페이지로 route 가드가 없으므로 `serviceInitialGuards`만 사용합니다.
 * `serviceRouteGuards`를 전달해도 조용히 무시되므로, 추후 route 가드가 필요해지면
 * `generateRouteServerSideGuards` + `ROUTE_DEFAULT_STATE`를 함께 추가하세요.
 *
 * Account wrapper도 동일하게 `GetServerSidePropsWrapperProps` 전체 타입을 사용하므로
 * 기존 패턴 일관성을 위해 별도 Onboarding 전용 타입은 정의하지 않습니다.
 */
export const getServerSidePropsOnboardingWrapper = (props?: GetServerSidePropsWrapperProps) => {
  const { serviceInitialGuards = [] } = props ?? {};

  const initialGuards = generateInitialServerSideGuards(serviceInitialGuards);

  return async (context: GetServerSidePropsContext) => {
    const { req } = context;
    const { url = '' } = req;
    /**
     * isInitial 분기 조건: Account 패턴(`!url.startsWith('/_next/data')`)을 따름
     *
     * - Account: `!url.startsWith('/_next/data')` → 2분기 (initial / route=stateless)
     * - Baguni:  `!url.startsWith('/_next')` → 3분기 (initial / route / else)
     *
     * Onboarding은 단일 페이지(route 가드 없음)이므로 Account처럼 2분기 구조를 사용.
     * `/_next/data`가 아닌 모든 요청을 initial로 간주해 가드 체인을 실행하고,
     * `/_next/data` 요청(클라이언트 사이드 내비게이션)은 빈 props를 반환한다.
     */
    const isInitial = !url.startsWith('/_next/data');

    if (isInitial) {
      return await executeStatefulServerSideGuards({
        context,
        guards: initialGuards,
        state: INITIAL_DEFAULT_STATE,
      });
    } else {
      return { props: {} };
    }
  };
};
